import { AsyncPipe, DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { PriceScalePipe } from '@zero/shared/ui-pipes';
import { InstrumentDetailsLinkComponent } from '@zero/shared/ui-web-components';
import { ButtonComponent, IconButtonComponent, IconComponent, NotificationComponent } from '@zero/shared/ui-web-design-system';
import { LeverageProductType } from '@zero/shared/util-models';
import { Derivative, TradingToolsFacade, UnderlyingSegment } from '@zero/trading-tools/domain';
import { commonRoutes } from 'constants/web-app-constants';
import { filter, firstValueFrom } from 'rxjs';
import { TrackingQueryParamsPipe } from '../../pipes/tracking-query-params.pipe';

@Component({
  selector: 'trading-tools-easy-trading-matrix',
  imports: [
    NotificationComponent,
    TranslocoPipe,
    IconComponent,
    AsyncPipe,
    DecimalPipe,
    NgTemplateOutlet,
    ButtonComponent,
    IconButtonComponent,
    RouterLink,
    NgbTooltipModule,
    InstrumentDetailsLinkComponent,
    PriceScalePipe,
    TrackingQueryParamsPipe,
  ],
  templateUrl: './easy-trading-matrix.component.html',
  styleUrl: './easy-trading-matrix.component.scss',
})
export class EasyTradingMatrixComponent implements OnInit, AfterViewInit {
  @ViewChild('activeUnderlying') public activeElement: ElementRef;

  public readonly tradingToolsFacade = inject(TradingToolsFacade);

  public activeUnderlyingSegment: UnderlyingSegment = 'STOCKS';
  public readonly segments: UnderlyingSegment[] = ['STOCKS', 'CURATED_UNDERLYINGS'];
  public readonly leverageSearchPath = commonRoutes.prelogin.leverageProducts;
  public readonly tradeMaskPath = commonRoutes.postlogin.buyAndSell;

  public readonly leverageProductType = LeverageProductType;
  public currentInvestmentDirection = LeverageProductType.CALL;

  readonly #destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.tradingToolsFacade.loadUnderlyings(this.activeUnderlyingSegment);
  }

  ngAfterViewInit(): void {
    this.tradingToolsFacade.selectedUnderlyingIsin$
      .pipe(
        filter((isin) => !!isin),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((isin) => {
        this.tradingToolsFacade.loadEasyTradingMatrix(isin);
        this.#ensureIsInView();
      });
  }

  public toggleUnderlyingsSegment(segment: UnderlyingSegment) {
    this.activeUnderlyingSegment = segment;
    this.tradingToolsFacade.loadUnderlyings(this.activeUnderlyingSegment);
  }

  public async navigateLeft() {
    const { underlyings, currentIndex } = await this.#getCurrentModel();
    this.tradingToolsFacade.selectUnderlying(underlyings[currentIndex === 0 ? underlyings.length - 1 : currentIndex - 1].isin);
  }

  public async navigateRight() {
    const { underlyings, currentIndex } = await this.#getCurrentModel();
    this.tradingToolsFacade.selectUnderlying(underlyings[currentIndex === underlyings.length - 1 ? 0 : currentIndex + 1].isin);
  }

  public toggleTooltip(tooltip, derivative: Derivative, type: LeverageProductType) {
    tooltip.isOpen()
      ? tooltip.close()
      : tooltip.open({
          product: type === LeverageProductType.CALL ? derivative?.call : derivative?.put,
          derivative,
          type,
        });
  }

  #ensureIsInView() {
    setTimeout(() => {
      this.activeElement && this.activeElement.nativeElement.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, 0);
  }

  async #getCurrentModel() {
    return {
      underlyings: await firstValueFrom(this.tradingToolsFacade.underlyings$),
      currentIndex: await firstValueFrom(this.tradingToolsFacade.currentUnderlyingIndex$),
    };
  }
}
